import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/utils/cn";

const inputVariants = cva(
  "block w-full rounded-lg border shadow-sm transition-colors focus:outline-none focus:ring-1",
  {
    variants: {
      variant: {
        default: [
          "border-gray-300 bg-white text-gray-900 placeholder-gray-400",
 "focus:border-ms-primary-500 focus:ring-ms-primary-500",
        ],
        error: [
          "border-red-300 bg-white text-gray-900 placeholder-gray-400",
          "focus:border-red-500 focus:ring-red-500",
        ],
      },
      inputSize: {
        sm: "px-3 py-1.5 text-sm",
        md: "px-3 py-2",
        lg: "px-4 py-3",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "md",
    },
  },
);

export interface FormInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  label?: string;
  error?: string;
  helperText?: string;
  labelClassName?: string;
  inputClassName?: string;
  rightElement?: React.ReactNode;
  /** Element to render to the right of the label (e.g., FieldMeta for edit history) */
  labelRightElement?: React.ReactNode;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      variant,
      inputSize,
      label,
      error,
      helperText,
      labelClassName,
      inputClassName,
      rightElement,
      labelRightElement,
      id,
      ...props
    },
    ref,
  ) => {
    const inputId = id || props.name;
    const effectiveVariant = error ? "error" : variant;

    return (
      <div className={cn("w-full", className)}>
        {(label || labelRightElement) && (
          <div className="mb-2 flex flex-wrap items-center gap-x-2 sm:flex-nowrap">
            {label && (
              <label
                htmlFor={inputId}
                className={cn(
                  "text-sm font-medium text-gray-700",
                  labelClassName,
                )}
              >
                {label}
              </label>
            )}
            {labelRightElement}
          </div>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            className={cn(
              inputVariants({ variant: effectiveVariant, inputSize }),
              rightElement && "pr-12",
              inputClassName,
            )}
            aria-invalid={!!error}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-helper`
                  : undefined
            }
            {...props}
          />
          {rightElement && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {rightElement}
            </div>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-red-600">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${inputId}-helper`} className="mt-1 text-xs text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

FormInput.displayName = "FormInput";

export { FormInput, inputVariants };
